import { useState } from 'react';
import { Search } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ModuleTogglePanel } from '@/components/dashboard/ModuleTogglePanel';

/**
 * The 4 toggle keys are the real setting names from cogs/security.py's
 * DASHBOARD_FEATURES, which bridge/config_registry.py's "security" module
 * validates against live (bridge/config.py's validate_toggle_patch) — a
 * typo'd key here would just be rejected server-side with a 422, not
 * silently accepted, but keeping these accurate avoids that trip entirely.
 */
const securityFeatures = [
  { key: 'scam_protection', label: 'Scam link protection', description: 'Flags known scam/phishing link patterns.' },
  { key: 'mention_protection', label: 'Mass mention protection', description: 'Flags messages that mention an excessive number of users.' },
  { key: 'anti_link', label: 'Anti-link', description: 'Flags any external link, regardless of destination.' },
  { key: 'badword_filter', label: 'Bad word filter', description: 'Flags messages matching the bad-word list.' },
] as const;

export function SecurityPage() {
  const [lookupId, setLookupId] = useState('');

  return (
    <div className="max-w-3xl space-y-6 pb-24">
      <div>
        <h1 className="text-h1 text-text-primary mb-1">Security</h1>
        <p className="text-small text-text-secondary">
          Per-user threat scoring (Security AI) and anti-nuke exemptions.
        </p>
      </div>

      <ModuleTogglePanel
        module="security"
        title="Security AI"
        permissionNote="Administrator permission required — matches the bot's own requirement exactly."
        features={[...securityFeatures]}
      />

      <ModuleTogglePanel
        module="antinuke"
        title="Anti-Nuke"
        permissionNote="Requires the bot owner or a server administrator — a stricter requirement than every other toggle on this page."
      />

      <Card>
        <CardHeader title="Heat score lookup" description="Look up a member's current threat score (0–10)." />
        <div className="flex gap-2">
          <Input
            placeholder="Member ID or @mention"
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            aria-label="Member ID or mention"
          />
          <Button variant="secondary" disabled>
            <Search size={16} aria-hidden="true" />
            Look up
          </Button>
        </div>
        <p className="text-small text-text-tertiary mt-2">
          Not yet supported by the Bridge — this would need a new read-only endpoint, out of scope for this pass.
        </p>
      </Card>

      <Card>
        <CardHeader title="Anti-Nuke whitelist" />
        <EmptyState
          icon={Search}
          title="Whitelist management isn't connected yet"
          description="The toggle above is real; adding/removing exempted members needs a separate Bridge endpoint not built in this pass. The trigger threshold itself is a bot-wide setting today, not per-server — see docs/V18_ARCHITECTURE_NOTES.md §3."
        />
      </Card>
    </div>
  );
}
